export const API_CONSTANTS = {
  MAX_RETRIES: 3,
  BASE_DELAY: 1000,
  REQUEST_TIMEOUT: 10000,
  CACHE_MAX_SIZE: 100,
};

export function calculateVat(amount, vatRate = 0.21) {
  return roundCurrency(amount * vatRate);
}

export function calculateTotalWithVat(amount, vatRate = 0.21) {
  return roundCurrency(amount + calculateVat(amount, vatRate));
}

export function calculateVatBreakdown(amount) {
  const vatHigh = roundCurrency(amount * 0.21);
  const vatLow = roundCurrency(amount * 0.09);
  const vatZero = roundCurrency(amount * 0);

  return {
    vatHigh,
    vatLow,
    vatZero,
    total: vatHigh + vatLow + vatZero,
  };
}

export function roundCurrency(amount) {
  return Math.round(amount * 100) / 100;
}

export function formatPrice(price, options = {}) {
  const {
    thousandSeparator = '.',
    decimalSeparator = ',',
    decimals = 2,
  } = options;

  const numPrice = parseFloat(price);
  if (isNaN(numPrice)) return '0,00';

  const roundedPrice = roundCurrency(numPrice);
  const priceString = roundedPrice.toFixed(decimals);

  const [wholePart, decimalPart] = priceString.split('.');

  const formattedWholePart = wholePart.replace(
    /\B(?=(\d{3})+(?!\d))/g,
    thousandSeparator,
  );

  if (decimalPart) {
    return `${formattedWholePart}${decimalSeparator}${decimalPart}`;
  }

  return formattedWholePart;
}

export function getPriceFormat() {
  if (
    typeof window !== 'undefined' &&
    typeof window.wc_price_format !== 'undefined'
  ) {
    return window.wc_price_format;
  }

  return {
    thousandSeparator: '.',
    decimalSeparator: ',',
    decimals: 2,
  };
}

export async function calculatePackagePrice(packageData) {
  let total = 0;

  if (packageData.products) {
    total += packageData.products.reduce(
      (sum, product) => sum + product.price * product.count,
      0,
    );
  }

  if (packageData.foodDrinks) {
    total += packageData.foodDrinks.reduce(
      (sum, food) => sum + food.price * (food.count || 1),
      0,
    );
  }

  if (packageData.packagingMode === 'product' && packageData.packagingProduct) {
    total += packageData.packagingProduct.price;
  } else if (packageData.packagingMode === 'box' && packageData.packaging) {
    total += packageData.packaging.price;
  }

  if (packageData.personalization && packageData.personalization.customDesign) {
    total += packageData.personalization.customDesignPrice;
  }

  if (packageData.personalization?.printing?.per_package?.total_ex_vat) {
    total += packageData.personalization.printing.per_package.total_ex_vat;
  } else if (packageData.personalization?.printing?.total_ex_vat) {
    total +=
      packageData.personalization.printing.total_ex_vat /
      (packageData.quantity || 1);
  }

  if (packageData.personalization?.selectedCard) {
    const cardQuantity = packageData.quantity || 1;
    const card = packageData.personalization.selectedCard;
    let cardPrice = card.price;

    if (card.tierPrice !== undefined) {
      cardPrice = card.tierPrice;
    } else {
      const tierPrice = await getCardUnitPrice(cardQuantity);
      if (tierPrice > 0) {
        cardPrice = tierPrice;
      }
    }

    total += cardPrice;
  }

  return roundCurrency(total);
}

export function getVatRate(product) {
  if (
    product.taxRate !== undefined &&
    product.taxRate !== null &&
    !isNaN(parseFloat(product.taxRate))
  ) {
    return parseFloat(product.taxRate);
  }

  const taxClass = product.tax_class || product.taxClass || '';

  if (
    taxClass.toLowerCase().includes('reduced') ||
    taxClass.toLowerCase().includes('low') ||
    taxClass.toLowerCase().includes('9') ||
    taxClass.toLowerCase().includes('laag') ||
    taxClass.toLowerCase().includes('verlaagd') ||
    taxClass.toLowerCase().includes('btw-laag')
  ) {
    return 0.09;
  }

  if (
    taxClass.toLowerCase().includes('zero') ||
    taxClass.toLowerCase().includes('0') ||
    taxClass.toLowerCase().includes('nul')
  ) {
    return 0;
  }

  if (
    product.name &&
    (product.name.toLowerCase().includes('tas') ||
      product.name.toLowerCase().includes('verpakking') ||
      product.name.toLowerCase().includes('doos') ||
      product.name.toLowerCase().includes('zak') ||
      product.name.toLowerCase().includes('box'))
  ) {
    return 0.09;
  }

  return 0.21;
}

export async function getVolumeDiscountPercent(quantity) {
  if (quantity < 25) return 0;

  try {
    const response = await fetch(`/api/volume-discount?quantity=${quantity}`);
    if (response.ok) {
      const data = await response.json();
      return data.discountPercent || 0;
    }
  } catch {
    // Silently fail, fallback to hardcoded discount
  }

  return getHardcodedVolumeDiscount(quantity);
}

export function getHardcodedVolumeDiscount(quantity) {
  if (quantity < 25) return 0;
  if (quantity <= 49) return 2;
  if (quantity <= 99) return 4;
  if (quantity <= 249) return 6;
  if (quantity <= 499) return 8;
  if (quantity <= 1000000) return 10;
  return 0;
}

/**
 * Calculate total number of cards across all items
 */
function calculateTotalCardQuantity(items) {
  let totalCards = 0;
  items.forEach((item) => {
    if (item.personalization?.selectedCard && item.quantity) {
      totalCards += item.quantity;
    }
  });
  return totalCards;
}

const WOOCOMMERCE_BASE_PRICE = 1.4;
const SUSPICIOUS_PRICE_THRESHOLD = 3.0;

export async function prepareItemsWithCardPrices(items) {
  const totalCards = calculateTotalCardQuantity(items);
  const cardUnitPrice = totalCards > 0 ? await getCardUnitPrice(totalCards) : 0;

  const result = items.map((item) => {
    if (item.personalization?.selectedCard) {
      const card = item.personalization.selectedCard;
      let originalPrice = card.originalPrice || card.price;

      // If no tier match and originalPrice looks like a tier price (too high),
      // reset to WooCommerce base price
      if (cardUnitPrice === 0 && originalPrice > SUSPICIOUS_PRICE_THRESHOLD) {
        originalPrice = WOOCOMMERCE_BASE_PRICE;
      }

      const finalPrice = cardUnitPrice > 0 ? cardUnitPrice : originalPrice;

      return {
        ...item,
        personalization: {
          ...item.personalization,
          selectedCard: {
            ...card,
            price: finalPrice,
            originalPrice: originalPrice,
            tierPrice: cardUnitPrice > 0 ? cardUnitPrice : undefined,
          },
        },
      };
    }
    return item;
  });

  return result;
}

export async function calculateUnifiedOrderTotal(
  items,
  volumeDiscountPercent = 0,
  shippingCost = 0,
) {
  if (!items || items.length === 0) {
    return {
      subtotalExVat: shippingCost,
      subtotalAfterDiscount: shippingCost,
      volumeDiscountPercent: 0,
      volumeDiscountAmount: 0,
      vatRates: new Map(),
      total: shippingCost,
      totalInclVat: shippingCost + shippingCost * 0.21,
      totalVat: shippingCost * 0.21,
    };
  }

  // Prepare items with correct card prices based on volume tiers
  const preparedItems = await prepareItemsWithCardPrices(items);

  const result = calculateVatBreakdownByProduct(
    preparedItems,
    volumeDiscountPercent,
    shippingCost,
  );

  return {
    subtotalExVat: result.subtotalExVat,
    subtotalAfterDiscount: result.subtotalAfterDiscount,
    volumeDiscountPercent: result.volumeDiscountPercent,
    volumeDiscountAmount: result.volumeDiscountAmount,
    vatRates: result.vatRates,
    total: result.total,
    totalInclVat: result.totalInclVat,
    totalVat: result.totalVat,
    // Include VAT breakdown per rate (CRITICAL for backend!)
    highVat: result.highVat,
    lowVat: result.lowVat,
    // Include shipping details
    shippingVat: result.shippingVat,
    shippingCost: result.shippingCost,
  };
}

export function getVatRateFromTaxClass(taxClass, product = null) {
  if (product) {
    return getVatRate(product);
  }

  if (!taxClass) return 0.21;

  const taxClassLower = taxClass.toLowerCase();

  if (
    taxClassLower.includes('reduced') ||
    taxClassLower.includes('low') ||
    taxClassLower.includes('9') ||
    taxClassLower.includes('laag') ||
    taxClassLower.includes('verlaagd') ||
    taxClassLower.includes('btw-laag')
  ) {
    return 0.09;
  }

  if (
    taxClassLower.includes('zero') ||
    taxClassLower.includes('0') ||
    taxClassLower.includes('nul')
  ) {
    return 0;
  }

  return 0.21;
}

export function calculateVatBreakdownByProduct(
  items,
  volumeDiscountPercent = 0,
  shippingCost = 0,
) {
  let totalItemsExVat = 0;
  let totalHighBase = 0;
  let totalLowBase = 0;
  let discountableHighBase = 0;
  let discountableLowBase = 0;

  items.forEach((item) => {
    const itemQuantity = item.quantity || 1;

    let unitHighBase = 0;
    let unitLowBase = 0;
    let unitDiscountableHigh = 0;
    let unitDiscountableLow = 0;

    function addProduct(price, vatRate, excluded) {
      if (vatRate === 0.21) {
        unitHighBase += price;
        if (!excluded) unitDiscountableHigh += price;
      } else if (vatRate === 0.09) {
        unitLowBase += price;
        if (!excluded) unitDiscountableLow += price;
      }
    }

    if (item.products) {
      item.products.forEach((product) => {
        const vatRate = getVatRate(product);
        const productPrice = product.price * product.count;
        addProduct(productPrice, vatRate, product.staffelExclude);
      });
    }

    if (item.foodDrinks) {
      item.foodDrinks.forEach((food) => {
        const vatRate = getVatRate(food);
        const foodPrice = food.price * (food.count || 1);
        addProduct(foodPrice, vatRate, food.staffelExclude);
      });
    }

    if (item.packaging && item.packagingMode === 'box') {
      const packagingVatRate = getVatRate(item.packaging);
      const packagingPrice = item.packaging.price;
      addProduct(
        packagingPrice,
        packagingVatRate,
        item.packaging.staffelExclude,
      );
    }

    if (item.packagingProduct && item.packagingMode === 'product') {
      const packagingVatRate = getVatRate(item.packagingProduct);
      const packagingPrice = item.packagingProduct.price;
      addProduct(
        packagingPrice,
        packagingVatRate,
        item.packagingProduct.staffelExclude,
      );
    }

    if (item.personalization && item.personalization.customDesign) {
      const personalizationVatRate = getVatRateFromTaxClass(
        item.personalization.customDesign.taxClass,
        item.personalization.customDesign,
      );
      const personalizationPrice = item.personalization.customDesignPrice;
      addProduct(personalizationPrice, personalizationVatRate, false);
    }

    if (item.personalization && item.personalization.selectedCard) {
      const cardVatRate = getVatRateFromTaxClass(
        item.personalization.selectedCard.taxClass,
        item.personalization.selectedCard,
      );
      const cardPrice = item.personalization.selectedCard.price;
      addProduct(cardPrice, cardVatRate, false);
    }

    if (itemQuantity > 1) {
      if (item.personalization?.printing?.per_package?.total_ex_vat) {
        unitHighBase += item.personalization.printing.per_package.total_ex_vat;
        unitDiscountableHigh +=
          item.personalization.printing.per_package.total_ex_vat;
      } else if (item.personalization?.printing?.total_ex_vat) {
        const printingCostPerUnit =
          item.personalization.printing.total_ex_vat / (itemQuantity || 1);
        unitHighBase += printingCostPerUnit;
        unitDiscountableHigh += printingCostPerUnit;
      }
    }

    totalItemsExVat += (unitHighBase + unitLowBase) * itemQuantity;
    totalHighBase += unitHighBase * itemQuantity;
    totalLowBase += unitLowBase * itemQuantity;
    discountableHighBase += unitDiscountableHigh * itemQuantity;
    discountableLowBase += unitDiscountableLow * itemQuantity;
  });

  const subtotalExVat = totalItemsExVat;

  const discountableBase = discountableHighBase + discountableLowBase;
  const volumeDiscountAmount = roundCurrency(
    discountableBase * (volumeDiscountPercent / 100),
  );

  let discountHigh = 0;
  let discountLow = 0;

  if (discountableBase > 0) {
    const highRatio = discountableHighBase / discountableBase;
    const lowRatio = discountableLowBase / discountableBase;

    discountHigh = roundCurrency(volumeDiscountAmount * highRatio);
    discountLow = roundCurrency(volumeDiscountAmount * lowRatio);
  }

  // Step 4: Calculate net grounds per rate
  const netHighBase = roundCurrency(totalHighBase - discountHigh);
  const netLowBase = roundCurrency(totalLowBase - discountLow);

  // Step 5: Calculate VAT per rate and round to 2 decimals
  const highVat = roundCurrency(netHighBase * 0.21);
  const lowVat = roundCurrency(netLowBase * 0.09);

  // Step 6: Add shipping
  const totalShippingVat =
    shippingCost > 0 ? roundCurrency(shippingCost * 0.21) : 0;

  // Step 7: Calculate final totals
  const subtotalAfterDiscount = roundCurrency(
    subtotalExVat - volumeDiscountAmount,
  );
  const totalExVat = roundCurrency(subtotalAfterDiscount + shippingCost);
  const totalVat = roundCurrency(highVat + lowVat + totalShippingVat);
  const totalInclVat = roundCurrency(totalExVat + totalVat);

  const vatRates = new Map();
  if (highVat > 0) vatRates.set(0.21, highVat);
  if (lowVat > 0) vatRates.set(0.09, lowVat);
  // Don't add shipping VAT to vatRates as it's shown separately

  const result = {
    subtotalExVat: roundCurrency(subtotalExVat),
    subtotalAfterDiscount: roundCurrency(subtotalAfterDiscount),
    originalSubtotalExVat: roundCurrency(subtotalExVat),
    volumeDiscountPercent,
    volumeDiscountAmount: roundCurrency(volumeDiscountAmount),
    discountHigh: roundCurrency(discountHigh),
    discountLow: roundCurrency(discountLow),
    netHighBase: roundCurrency(netHighBase),
    netLowBase: roundCurrency(netLowBase),
    vatRates,
    total: roundCurrency(subtotalAfterDiscount),
    totalExVat: roundCurrency(totalExVat),
    totalInclVat: roundCurrency(totalInclVat),
    totalVat: roundCurrency(totalVat),
    highVat: roundCurrency(highVat),
    lowVat: roundCurrency(lowVat),
    shippingVat: roundCurrency(totalShippingVat),
    shippingCost: roundCurrency(shippingCost),
  };

  return result;
}

export async function getPrintUnitPrice(method, qty, retryCount = 0) {
  const { MAX_RETRIES, BASE_DELAY, REQUEST_TIMEOUT } = API_CONSTANTS;

  try {
    let response;

    // Check if AbortController is supported
    if (typeof AbortController !== 'undefined') {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

      response = await fetch(
        `/api/printing/unit-price?method=${encodeURIComponent(method)}&qty=${qty}`,
        {
          signal: controller.signal,
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );

      clearTimeout(timeoutId);
    } else {
      // Fallback for older browsers
      response = await fetch(
        `/api/printing/unit-price?method=${encodeURIComponent(method)}&qty=${qty}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );
    }

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.unit_price || data.unitPrice || 0;
  } catch {
    if (retryCount < MAX_RETRIES) {
      const delay = BASE_DELAY * Math.pow(2, retryCount);
      await new Promise((resolve) => setTimeout(resolve, delay));
      return getPrintUnitPrice(method, qty, retryCount + 1);
    }

    return 0;
  }
}

const cardPriceCache = new Map();
const cardPriceInFlight = new Map();

export async function getCardUnitPrice(qty, retryCount = 0) {
  const { MAX_RETRIES, BASE_DELAY, REQUEST_TIMEOUT, CACHE_MAX_SIZE } =
    API_CONSTANTS;
  const cacheKey = `qty_${qty}`;

  if (retryCount === 0 && cardPriceCache.has(cacheKey)) {
    return cardPriceCache.get(cacheKey);
  }

  if (retryCount === 0 && cardPriceInFlight.has(cacheKey)) {
    return cardPriceInFlight.get(cacheKey);
  }

  const requestPromise = (async () => {
    try {
      const url = `/api/cards/unit-price?qty=${qty}`;
      const fetchOptions = {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      };

      let response;
      if (typeof AbortController !== 'undefined') {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);
        fetchOptions.signal = controller.signal;

        response = await fetch(url, fetchOptions);
        clearTimeout(timeoutId);
      } else {
        response = await fetch(url, fetchOptions);
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const unitPrice = data.unit_price || data.unitPrice || 0;

      if (retryCount === 0) {
        cardPriceCache.set(cacheKey, unitPrice);
        if (cardPriceCache.size > CACHE_MAX_SIZE) {
          const firstKey = cardPriceCache.keys().next().value;
          cardPriceCache.delete(firstKey);
        }
      }

      return unitPrice;
    } catch {
      if (retryCount < MAX_RETRIES) {
        const delay = BASE_DELAY * Math.pow(2, retryCount);
        await new Promise((resolve) => setTimeout(resolve, delay));
        return getCardUnitPrice(qty, retryCount + 1);
      }

      if (retryCount === MAX_RETRIES) {
        cardPriceCache.set(cacheKey, 0);
      }
      return 0;
    } finally {
      if (retryCount === 0) {
        cardPriceInFlight.delete(cacheKey);
      }
    }
  })();

  if (retryCount === 0) {
    cardPriceInFlight.set(cacheKey, requestPromise);
  }

  return requestPromise;
}

export async function calculatePrintingCosts(counts) {
  const result = { sticker: {}, zeefdruk: {}, graveren: {}, totalExVat: 0 };

  // Prepare API calls for methods with quantities > 0
  const apiCalls = [];
  const methods = ['sticker', 'zeefdruk', 'graveren'];

  for (const method of methods) {
    const qty = counts[method] || 0;
    if (qty <= 0) {
      result[method] = { qty: 0, unit: 0, total: 0 };
      continue;
    }

    apiCalls.push({ method, qty });
  }

  if (apiCalls.length === 0) {
    return result;
  }

  // Execute API calls in parallel with controlled concurrency
  const batchSize = 2; // Process 2 calls at a time to avoid rate limiting
  const batches = [];

  for (let i = 0; i < apiCalls.length; i += batchSize) {
    batches.push(apiCalls.slice(i, i + batchSize));
  }

  // Process batches sequentially, but calls within each batch in parallel
  for (const batch of batches) {
    const batchPromises = batch.map(async ({ method, qty }) => {
      const unit = await getPrintUnitPrice(method, qty);
      const total = unit * qty;
      return { method, qty, unit, total };
    });

    const batchResults = await Promise.all(batchPromises);

    // Process results
    batchResults.forEach(({ method, qty, unit, total }) => {
      result[method] = { qty, unit, total };
      result.totalExVat += total;
    });

    // Small delay between batches to prevent rate limiting
    if (batches.length > 1) {
      await new Promise((resolve) => setTimeout(resolve, 50));
    }
  }

  return result;
}

export function calculatePrintingCounts(
  selectedProducts,
  packageQuantity,
  productPersonalization = {},
) {
  const counts = { sticker: 0, zeefdruk: 0, graveren: 0 };

  if (!selectedProducts || !Array.isArray(selectedProducts)) {
    return counts;
  }

  selectedProducts.forEach((product) => {
    // Skip null or undefined products
    if (!product || typeof product !== 'object') {
      return;
    }

    const productCount = product?.count || product?.quantity || 0;
    if (productCount > 0) {
      const personalization = productPersonalization[product.id] || {};
      if (personalization.sticker) counts.sticker += productCount;
      if (personalization.zeefdruk) counts.zeefdruk += productCount;
      if (personalization.graveren) counts.graveren += productCount;
    }
  });

  return {
    sticker: counts.sticker * packageQuantity,
    zeefdruk: counts.zeefdruk * packageQuantity,
    graveren: counts.graveren * packageQuantity,
  };
}
